import { Capacitor } from '@capacitor/core'

// Duryzehin API Client with Antiforgery & Auth handling
export function getApiBase(): string {
  const custom = typeof localStorage !== 'undefined' ? localStorage.getItem('dz_custom_api_url') : null
  if (custom) return custom.replace(/\/$/, '')
  const envUrl = import.meta.env.VITE_API_URL
  if (envUrl) return envUrl.replace(/\/$/, '')
  if (Capacitor.isNativePlatform()) {
    return 'https://test.duryzehin.com'
  }
  return ''
}

export function setCustomApiBase(url: string) {
  if (!url) {
    localStorage.removeItem('dz_custom_api_url')
  } else {
    localStorage.setItem('dz_custom_api_url', url.trim().replace(/\/$/, ''))
  }
}

export async function testApiConnection(customUrl?: string): Promise<{ ok: boolean; message: string }> {
  const base = (customUrl !== undefined ? customUrl : getApiBase()).replace(/\/$/, '')
  const target = base ? `${base}/api/admin/auth/me` : '/api/admin/auth/me'
  try {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 4000)
    const res = await fetch(target, {
      method: 'GET',
      credentials: 'include',
      signal: controller.signal
    })
    clearTimeout(timer)
    // 200 (authenticated) or 401 (server reachable, unauthenticated) means the API is online!
    if (res.status === 200 || res.status === 401) {
      return { ok: true, message: `Servere birikildi (${res.status})` }
    }
    return { ok: false, message: `Server jogap berdi: HTTP ${res.status}` }
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      return { ok: false, message: 'Server jogap bermedi (wagt gutardy - 4s)' }
    }
    return { ok: false, message: err?.message || 'Baglanyşyk ýalňyşlygy (NetworkError)' }
  }
}

function getCsrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null
  if (meta && meta.content) {
    return meta.content
  }
  // Try cookie fallback
  const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : ''
}

export async function request<T = any>(url: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers || {})

  if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  const token = getCsrfToken()
  if (token && !headers.has('RequestVerificationToken')) {
    headers.set('RequestVerificationToken', token)
  }

  // Ensure cookies (ASP.NET Identity authentication cookie) are included
  const fetchOptions: RequestInit = {
    ...options,
    headers,
    credentials: 'include'
  }

  const apiBase = getApiBase()
  const targetUrl = url.startsWith('/') && apiBase ? `${apiBase}${url}` : url
  const response = await fetch(targetUrl, fetchOptions)

  if (response.status === 401 || (response.redirected && (response.url.includes('/Identity/Account/Login') || response.url.includes('/login')))) {
    // If not the login API itself, redirect to Vue login page
    if (!url.includes('/api/admin/auth/login')) {
      if (window.location.pathname !== '/login') {
        const returnUrl = encodeURIComponent(window.location.pathname + window.location.search)
        window.location.href = `/login?redirect=${returnUrl}`
      }
    }
    throw new Error('Unauthorized')
  }

  if (response.status === 403) {
    throw new Error('Access denied: You do not have permission to perform this action.')
  }

  if (!response.ok) {
    const errorText = await response.text()
    let errorMsg = errorText
    try {
      const errJson = JSON.parse(errorText)
      errorMsg = errJson.message || errJson.title || errorText
    } catch {
      // Keep plain text
    }
    throw new Error(errorMsg || `Request failed with status ${response.status}`)
  }

  // Check if response has JSON content
  const contentType = response.headers.get('content-type')
  if (contentType && contentType.includes('application/json')) {
    return response.json()
  }

  if (contentType && contentType.includes('text/html')) {
    const text = await response.text()
    if (text.includes('/Identity/Account/Login') || text.includes('id="login-email"') || text.includes('__RequestVerificationToken')) {
      if (!url.includes('/api/admin/auth/login')) {
        if (window.location.pathname !== '/login') {
          const returnUrl = encodeURIComponent(window.location.pathname + window.location.search)
          window.location.href = `/login?redirect=${returnUrl}`
        }
      }
      throw new Error('Unauthorized')
    }
    return text as unknown as T
  }

  return response.text() as unknown as T
}

export const api = {
  get: <T = any>(url: string) => request<T>(url, { method: 'GET' }),
  post: <T = any>(url: string, data?: any) =>
    request<T>(url, {
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data)
    }),
  put: <T = any>(url: string, data?: any) =>
    request<T>(url, {
      method: 'PUT',
      body: data instanceof FormData ? data : JSON.stringify(data)
    }),
  patch: <T = any>(url: string, data?: any) =>
    request<T>(url, {
      method: 'PATCH',
      body: data ? (data instanceof FormData ? data : JSON.stringify(data)) : undefined
    }),
  delete: <T = any>(url: string, data?: any) =>
    request<T>(url, {
      method: 'DELETE',
      body: data ? (data instanceof FormData ? data : JSON.stringify(data)) : undefined
    })
}
